import React, { useEffect, useMemo, useState } from 'react';

const Icons = {
  Search: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  Fire: () => (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
    </svg>
  ),
  Pill: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l4-4m-4 4a4 4 0 105.657-5.657l-4 4A4 4 0 0010 14z" />
    </svg>
  )
};

const CSV_COLUMNS = [
  "gene_symbol",
  "variant_id",
  "hgvs_c",
  "hgvs_p",
  "chromosome",
  "position_grch38",
  "position_grch37",
  "ref_allele",
  "alt_allele",
  "mutation_type",
  "cancer_types",
  "source_database",
  "pmid_or_doi",
  "sample_count",
  "mutation_frequency",
  "total_samples_screened",
  "hotspot_status",
  "recurrence_level",
  "driver_status",
  "oncogene_tsg",
  "functional_impact_score",
  "oncokb_level",
  "clinvar_significance",
  "prediction_tools",
  "protein_domain",
  "functional_consequence",
  "affected_pathway",
  "mechanism_summary",
  "structural_impact",
  "actionable",
  "associated_drugs",
  "resistance_mutation",
  "resistance_to",
  "clinical_trials",
  "fda_approved_indication",
  "somatic_germline",
  "hereditary_syndrome",
  "penetrance",
  "cosmic_url",
  "cbioportal_url",
  "clinvar_url",
  "oncokb_url",
  "uniprot_url",
  "ensembl_url"
];

const MutationCatalogTable = () => {
  const [data, setData] = useState([]);
  const [oncotree, setOncotree] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [filters, setFilters] = useState({
    search: "",
    chromosome: "",
    cancerGroup: "",
    cancerType: "",
    pathway: "",
    driverStatus: "",
    geneRole: "",
    mutationType: "",
    actionability: "",
    frequencyMin: "",
    oncokbLevel: "",
  });
  const [cancerGroupBy] = useState("mainType");
  const [sortBy, setSortBy] = useState({ field: "mutation_frequency", direction: "desc" });

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const [catalogRes, oncotreeRes] = await Promise.all([
          fetch("/data/mutation_catalog.json"),
          fetch("/data/oncotree.json")
        ]);
        if (!catalogRes.ok) throw new Error("Failed to load mutation_catalog.json");
        const catalog = await catalogRes.json();
        const oncotreeMap = oncotreeRes.ok ? await oncotreeRes.json() : {};
        if (!cancelled) {
          setData(Array.isArray(catalog) ? catalog : []);
          setOncotree(oncotreeMap || {});
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load catalog");
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  const oncotreeAncestors = useMemo(() => {
    const ancestors = {};
    const getAncestors = (code) => {
      const list = [];
      let current = oncotree[code];
      while (current && current.parent) {
        list.push(current.parent);
        current = oncotree[current.parent];
      }
      return list;
    };
    Object.keys(oncotree).forEach(code => {
      ancestors[code] = getAncestors(code);
    });
    return ancestors;
  }, [oncotree]);

  const filterOptions = useMemo(() => {
    const chromosomes = new Set();
    const cancerTypes = new Map();
    const cancerTypeCounts = new Map();
    const cancerTypeNames = new Map();
    const pathways = new Set();
    const driverStatuses = new Set();
    const geneRoles = new Set();
    const mutationTypes = new Set();
    const oncokbLevels = new Set();

    data.forEach(row => {
      if (row.chromosome) chromosomes.add(String(row.chromosome));
      if (row.affected_pathway) pathways.add(row.affected_pathway);
      if (row.driver_status) driverStatuses.add(row.driver_status);
      if (row.oncogene_tsg) geneRoles.add(row.oncogene_tsg);
      if (row.mutation_type) mutationTypes.add(row.mutation_type);
      if (row.oncokb_level) oncokbLevels.add(row.oncokb_level);
      (row.cancer_types || []).forEach(ct => {
        if (!ct || !ct.code) return;
        cancerTypeCounts.set(ct.code, (cancerTypeCounts.get(ct.code) || 0) + 1);
        if (ct.name) cancerTypeNames.set(ct.code, ct.name);
      });
    });

    const topCancerCodes = Array.from(cancerTypeCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 50)
      .map(([code]) => code);

    topCancerCodes.forEach(code => {
      const node = oncotree[code] || {};
      const name = node.name || cancerTypeNames.get(code) || code;
      cancerTypes.set(code, {
        code,
        name,
        mainType: node.mainType || "",
        tissue: node.tissue || "",
        count: cancerTypeCounts.get(code) || 0
      });
    });

    const cancerTypeOptions = Array.from(cancerTypes.values())
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

    return {
      chromosomes: Array.from(chromosomes).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
      cancerTypes: cancerTypeOptions,
      pathways: Array.from(pathways).sort(),
      driverStatuses: Array.from(driverStatuses).sort(),
      geneRoles: Array.from(geneRoles).sort(),
      mutationTypes: Array.from(mutationTypes).sort(),
      oncokbLevels: Array.from(oncokbLevels).sort(),
    };
  }, [data, oncotree]);

  const groupedCancerTypes = useMemo(() => {
    const groups = new Map();
    const groupKeyFor = (ct) => {
      return ct.mainType || "Other";
    };

    filterOptions.cancerTypes.forEach(ct => {
      const groupKey = groupKeyFor(ct);
      if (!groups.has(groupKey)) groups.set(groupKey, []);
      groups.get(groupKey).push(ct);
    });

    return Array.from(groups.entries())
      .map(([label, items]) => ({
        label,
        items: items.sort((a, b) => a.name.localeCompare(b.name))
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filterOptions.cancerTypes, cancerGroupBy]);

  const cancerGroupOptions = useMemo(() => {
    const counts = new Map();
    filterOptions.cancerTypes.forEach(ct => {
      const key = ct.mainType || "Other";
      const prev = counts.get(key) || 0;
      counts.set(key, prev + (ct.count || 0));
    });
    return Array.from(counts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }, [filterOptions.cancerTypes]);


  const filtered = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const freqMin = filters.frequencyMin ? parseFloat(filters.frequencyMin) : null;

    return data.filter(row => {
      if (filters.chromosome && String(row.chromosome || "") !== filters.chromosome) return false;
      if (filters.pathway && (row.affected_pathway || "") !== filters.pathway) return false;
      if (filters.driverStatus && (row.driver_status || "") !== filters.driverStatus) return false;
      if (filters.geneRole && (row.oncogene_tsg || "") !== filters.geneRole) return false;
      if (filters.mutationType && (row.mutation_type || "") !== filters.mutationType) return false;
      if (filters.actionability && (row.actionable || "") !== filters.actionability) return false;
      if (filters.oncokbLevel && (row.oncokb_level || "") !== filters.oncokbLevel) return false;

      if (filters.cancerGroup) {
        const codes = (row.cancer_types || []).map(ct => ct.code).filter(Boolean);
        const match = codes.some(code => {
          const node = oncotree[code];
          const groupValue = node?.mainType || "Other";
          return groupValue === filters.cancerGroup;
        });
        if (!match) return false;
      }

      if (filters.cancerType) {
        const codes = (row.cancer_types || []).map(ct => ct.code);
        const match = codes.some(code => code === filters.cancerType || (oncotreeAncestors[code] || []).includes(filters.cancerType));
        if (!match) return false;
      }

      if (freqMin !== null) {
        const freq = row.mutation_frequency;
        if (typeof freq !== "number" || freq < freqMin) return false;
      }

      if (search) {
        const searchHaystack = [
          row.gene_symbol,
          row.hgvs_p,
          row.hgvs_c,
          (row.cancer_types || []).map(ct => ct.name).join(" "),
          row.affected_pathway,
          row.mutation_type,
          row.driver_status,
        ].join(" ").toLowerCase();
        if (!searchHaystack.includes(search)) return false;
      }

      return true;
    });
  }, [data, filters, oncotreeAncestors, oncotree]);

  const sorted = useMemo(() => {
    const rows = [...filtered];
    const { field, direction } = sortBy;
    const dir = direction === "asc" ? 1 : -1;

    rows.sort((a, b) => {
      const va = a[field];
      const vb = b[field];
      if (va == null && vb == null) return 0;
      if (va == null) return 1 * dir;
      if (vb == null) return -1 * dir;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });

    return rows;
  }, [filtered, sortBy]);

  const getGeneLink = (row) => {
    return row.oncokb_url || row.cbioportal_url || row.uniprot_url || "";
  };

  const getVariantLink = (row) => {
    return row.clinvar_url || row.cbioportal_url || row.oncokb_url || row.ensembl_url || "";
  };

  const toggleExpanded = (variantId) => {
    const next = new Set(expanded);
    if (next.has(variantId)) next.delete(variantId);
    else next.add(variantId);
    setExpanded(next);
  };

  const exportCsv = () => {
    const lines = [CSV_COLUMNS.join(",")];
    sorted.forEach(row => {
      const values = CSV_COLUMNS.map(col => {
        let value = row[col];
        if (col === "cancer_types" && Array.isArray(value)) {
          value = value.map(ct => `${ct.code}:${ct.name}`).join("|");
        } else if (Array.isArray(value)) {
          value = value.join("|");
        }
        if (value == null) value = "";
        const str = String(value).replace(/"/g, '""');
        return `"${str}"`;
      });
      lines.push(values.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "mutation_catalog_filtered.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const buildCitations = (row) => {
    const tokens = [];
    (row.pmid_or_doi || []).forEach(item => {
      if (!item) return;
      const firstSplit = String(item).split(/[;|]/);
      firstSplit.forEach(part => {
        const subparts = part.split(",");
        subparts.forEach(token => {
          const trimmed = token.trim();
          if (!trimmed) return;
          if (/^\d+(?:\s+\d+)+$/.test(trimmed)) {
            trimmed.split(/\s+/).forEach(num => tokens.push(num));
          } else {
            tokens.push(trimmed);
          }
        });
      });
    });

    const seen = new Set();
    const citations = [];
    const addCitation = (label, url = "") => {
      const key = `${label}|${url}`;
      if (seen.has(key)) return;
      seen.add(key);
      citations.push({ label, url });
    };

    tokens.forEach(token => {
      const raw = token.trim();
      if (!raw) return;

      let match = raw.match(/pmid[:\s]*(\d+)/i);
      if (match) {
        const id = match[1];
        addCitation(`PMID ${id}`, `https://pubmed.ncbi.nlm.nih.gov/${id}/`);
        return;
      }
      if (/^\d{5,}$/.test(raw)) {
        addCitation(`PMID ${raw}`, `https://pubmed.ncbi.nlm.nih.gov/${raw}/`);
        return;
      }
      match = raw.match(/doi[:\s]*(.+)/i);
      if (match) {
        const doi = match[1].trim();
        addCitation(`DOI ${doi}`, `https://doi.org/${doi}`);
        return;
      }
      if (/^10\.\d{4,9}\/\S+$/i.test(raw)) {
        addCitation(`DOI ${raw}`, `https://doi.org/${raw}`);
        return;
      }
      match = raw.match(/clinvar[:\s]*(\d+)/i);
      if (match) {
        const id = match[1];
        addCitation(`ClinVar ${id}`, `https://www.ncbi.nlm.nih.gov/clinvar/variation/${id}/`);
        return;
      }
      match = raw.match(/cbioportal[:\s]*([\w.-]+)/i);
      if (match) {
        const studyId = match[1];
        addCitation(`cBioPortal ${studyId}`, `https://www.cbioportal.org/study/summary?id=${studyId}`);
        return;
      }
      match = raw.match(/cancerhotspots[:\s]*(.+)/i);
      if (match) {
        addCitation(`Cancer Hotspots ${match[1]}`, "https://www.cancerhotspots.org/");
        return;
      }

      addCitation(raw);
    });

    return citations;
  };

  if (loading) {
    return <div className="text-gray-500">Loading mutation catalog...</div>;
  }
  if (error) {
    return <div className="text-red-600">{error}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-2 text-gray-700">
            <Icons.Search />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search gene, variant, cancer type, pathway"
              className="w-full md:w-96 border rounded px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sortBy.field}
              onChange={(e) => setSortBy({ ...sortBy, field: e.target.value })}
              className="border rounded px-3 py-2 text-sm"
            >
              <option value="mutation_frequency">Sort: Frequency</option>
              <option value="cancer_type_count">Sort: # Cancer Types</option>
              <option value="functional_impact_score">Sort: Impact Score</option>
              <option value="actionability_level_numeric">Sort: Actionability</option>
            </select>
            <button
              onClick={() => setSortBy({ ...sortBy, direction: sortBy.direction === 'asc' ? 'desc' : 'asc' })}
              className="px-3 py-2 border rounded text-sm"
            >
              {sortBy.direction === 'asc' ? 'Asc' : 'Desc'}
            </button>
            <button
              onClick={exportCsv}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Export CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <select
            value={filters.chromosome}
            onChange={(e) => setFilters({ ...filters, chromosome: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">Chromosome (All)</option>
            {filterOptions.chromosomes.map(chr => (
              <option key={chr} value={chr}>{chr}</option>
            ))}
          </select>

          <div className="space-y-2">
            <select
              value={filters.cancerGroup}
              onChange={(e) => setFilters({ ...filters, cancerGroup: e.target.value })}
              className="border rounded px-3 py-2"
            >
              <option value="">Cancer Group (All)</option>
              {cancerGroupOptions.map(group => (
                <option key={group.label} value={group.label}>{group.label} • {group.count}</option>
              ))}
            </select>
            <select
              value={filters.cancerType}
              onChange={(e) => setFilters({ ...filters, cancerType: e.target.value })}
              className="border rounded px-3 py-2"
            >
              <option value="">Cancer Type (All)</option>
              {groupedCancerTypes.map(group => (
                <optgroup key={group.label} label={`${group.label} (${group.items.length})`}>
                  {group.items.map(ct => (
                    <option key={ct.code} value={ct.code}>{ct.name} ({ct.code}) • {ct.count}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <select
            value={filters.pathway}
            onChange={(e) => setFilters({ ...filters, pathway: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">Pathway (All)</option>
            {filterOptions.pathways.map(path => (
              <option key={path} value={path}>{path}</option>
            ))}
          </select>

          <select
            value={filters.driverStatus}
            onChange={(e) => setFilters({ ...filters, driverStatus: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">Driver Status (All)</option>
            {filterOptions.driverStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          <select
            value={filters.geneRole}
            onChange={(e) => setFilters({ ...filters, geneRole: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">Gene Role (All)</option>
            {filterOptions.geneRoles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>

          <select
            value={filters.mutationType}
            onChange={(e) => setFilters({ ...filters, mutationType: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">Mutation Type (All)</option>
            {filterOptions.mutationTypes.map(mt => (
              <option key={mt} value={mt}>{mt}</option>
            ))}
          </select>

          <select
            value={filters.actionability}
            onChange={(e) => setFilters({ ...filters, actionability: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">Actionability (All)</option>
            <option value="Yes">Actionable</option>
            <option value="No">Not Actionable</option>
          </select>

          <input
            type="number"
            step="0.001"
            value={filters.frequencyMin}
            onChange={(e) => setFilters({ ...filters, frequencyMin: e.target.value })}
            placeholder="Min frequency (0.01)"
            className="border rounded px-3 py-2"
          />

          <select
            value={filters.oncokbLevel}
            onChange={(e) => setFilters({ ...filters, oncokbLevel: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">OncoKB Level (All)</option>
            {filterOptions.oncokbLevels.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap gap-2 text-sm text-gray-600">
          <span>Results: {sorted.length}</span>
          <span>•</span>
          <span>Sorted by {sortBy.field.replace(/_/g, " ")} ({sortBy.direction})</span>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Gene</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Variant</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Cancer Type</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Driver</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Frequency</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Role</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Actionable</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {sorted.slice(0, 500).map(row => {
              const citations = buildCitations(row);
              return (
              <React.Fragment key={row.variant_id}>
                <tr
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleExpanded(row.variant_id)}
                >
                  <td className="px-4 py-2 font-semibold text-indigo-600">
                    <div className="flex items-center gap-1">
                      {getGeneLink(row) ? (
                        <a
                          href={getGeneLink(row)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline"
                        >
                          {row.gene_symbol}
                        </a>
                      ) : (
                        row.gene_symbol
                      )}
                      {row.hotspot_status === "Yes" && <span className="text-orange-500"><Icons.Fire /></span>}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="font-medium">
                      {getVariantLink(row) ? (
                        <a
                          href={getVariantLink(row)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline"
                        >
                          {row.hgvs_p || row.hgvs_c || "-"}
                        </a>
                      ) : (
                        row.hgvs_p || row.hgvs_c || "-"
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{row.hgvs_c || ""}</div>
                  </td>
                  <td className="px-4 py-2">
                    {(row.cancer_types || []).slice(0, 2).map(ct => (
                      <span key={ct.code} className="inline-block mr-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{ct.name}</span>
                    ))}
                    {(row.cancer_types || []).length > 2 && (
                      <span className="text-xs text-gray-400">+{(row.cancer_types || []).length - 2}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm">{row.driver_status}</td>
                  <td className="px-4 py-2 text-sm">
                    {typeof row.mutation_frequency === "number" ? (row.mutation_frequency * 100).toFixed(2) + "%" : "-"}
                  </td>
                  <td className="px-4 py-2 text-sm">{row.oncogene_tsg}</td>
                  <td className="px-4 py-2 text-sm">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${row.actionable === "Yes" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {row.actionable === "Yes" ? <Icons.Pill /> : null}
                      {row.actionable === "Yes" ? "Yes" : "No"}
                    </span>
                  </td>
                </tr>
                {expanded.has(row.variant_id) && (
                  <tr className="bg-gray-50">
                    <td colSpan={7} className="px-4 py-3 text-sm">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <div className="text-gray-500">Mechanism</div>
                          <div className="font-medium">{row.mechanism_summary || "-"}</div>
                          <div className="mt-2 text-gray-500">Domain</div>
                          <div className="font-medium">{row.protein_domain || "-"}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Pathway</div>
                          <div className="font-medium">{row.affected_pathway || "-"}</div>
                          <div className="mt-2 text-gray-500">Sources</div>
                          <div className="font-medium">{(row.source_database || []).join(", ")}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Citations</div>
                          <div className="mt-1 flex flex-wrap gap-2">
                            {citations.slice(0, 8).map(citation => (
                              citation.url ? (
                                <a
                                  key={`${citation.label}-${citation.url}`}
                                  href={citation.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs hover:underline"
                                >
                                  {citation.label}
                                </a>
                              ) : (
                                <span
                                  key={`${citation.label}-${citation.url}`}
                                  className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs"
                                >
                                  {citation.label}
                                </span>
                              )
                            ))}
                            {citations.length === 0 && (
                              <span className="text-xs text-gray-400">No citations</span>
                            )}
                          </div>
                          <div className="mt-3 text-gray-500">Links</div>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {row.cbioportal_url && (
                              <a className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs" href={row.cbioportal_url} target="_blank" rel="noreferrer">cBioPortal</a>
                            )}
                            {row.clinvar_url && (
                              <a className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs" href={row.clinvar_url} target="_blank" rel="noreferrer">ClinVar</a>
                            )}
                            {row.oncokb_url && (
                              <a className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs" href={row.oncokb_url} target="_blank" rel="noreferrer">OncoKB</a>
                            )}
                            {row.uniprot_url && (
                              <a className="px-2 py-1 bg-gray-50 text-gray-700 rounded text-xs" href={row.uniprot_url} target="_blank" rel="noreferrer">UniProt</a>
                            )}
                            {row.ensembl_url && (
                              <a className="px-2 py-1 bg-yellow-50 text-yellow-700 rounded text-xs" href={row.ensembl_url} target="_blank" rel="noreferrer">Ensembl</a>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )})}
          </tbody>
        </table>
        {sorted.length > 500 && (
          <div className="p-3 text-center text-gray-500 text-sm">
            Showing 500 of {sorted.length}. Refine filters to narrow results.
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="text-xs text-gray-500">
          Data sources: cBioPortal (TCGA Pan-Cancer Atlas), Cancer Hotspots, ClinVar, IntOGen, OncoTree.
        </div>
      </div>
    </div>
  );
};

export default MutationCatalogTable;
